/**
 * App-wide typeface switcher — the sibling of lib/palette.ts.
 *
 * A font theme is a PAIRING, not a single family: the app runs bilingual and
 * styles/tailwind.css swaps `html[lang='ar'] / [dir='rtl']` onto
 * --font-sans-arabic, so a theme has to answer for both scripts. Most Latin UI
 * faces carry no Arabic glyphs, so each entry names a Latin family AND an
 * Arabic one; applyFont writes both custom properties on :root and every
 * surface that reads var(--font-sans) / var(--font-sans-arabic) follows —
 * shell, pages, dialogs, print views. --font-mono is deliberately untouched:
 * codes and figures keep their tabular face whatever the UI font is.
 *
 * Font files are not paid for up front. Only the four families already in
 * styles/fonts.ts ship in the entry chunk; every other one is a dynamic import
 * of its @fontsource CSS, fetched the first time it is previewed or applied.
 */

export interface FontTheme {
  id: string;
  /** Display name, rendered by the picker in the face itself. */
  name: string;
  /** i18n key when the name is a word rather than a brand (System). */
  labelKey?: string;
  /** CSS family for Latin text — also the picker's preview face. */
  latin: string;
  /** CSS family for Arabic text, and the per-glyph fallback inside Latin runs. */
  arabic: string;
  /** Loads the @fontsource CSS. Absent ⇒ already in the entry bundle. */
  load?: () => Promise<unknown>;
}

/**
 * The offered faces. @fontsource-variable packages register under a
 * "<Name> Variable" family — that string, not the display name, is what CSS
 * has to match.
 */
export const FONTS: FontTheme[] = [
  // --- bundled (styles/fonts.ts) ---
  // @fontsource/geist-sans registers as "Geist Sans", not "Geist".
  { id: 'geist', name: 'Geist', latin: 'Geist Sans', arabic: 'Cairo' },
  { id: 'inter', name: 'Inter', latin: 'Inter', arabic: 'IBM Plex Sans Arabic' },
  { id: 'poppins', name: 'Poppins', latin: 'Poppins', arabic: 'Cairo' },
  { id: 'cairo', name: 'Cairo', latin: 'Cairo', arabic: 'Cairo' },
  // --- fetched on demand ---
  {
    id: 'ibm-plex',
    name: 'IBM Plex Sans',
    latin: 'IBM Plex Sans',
    arabic: 'IBM Plex Sans Arabic',
    load: () => Promise.all([
      import('@fontsource/ibm-plex-sans/400.css'),
      import('@fontsource/ibm-plex-sans/500.css'),
      import('@fontsource/ibm-plex-sans/600.css'),
      import('@fontsource/ibm-plex-sans/700.css'),
    ]),
  },
  {
    id: 'jakarta',
    name: 'Plus Jakarta Sans',
    latin: 'Plus Jakarta Sans Variable',
    arabic: 'Cairo',
    load: () => import('@fontsource-variable/plus-jakarta-sans/index.css'),
  },
  {
    id: 'rubik',
    name: 'Rubik',
    latin: 'Rubik Variable',
    arabic: 'Rubik Variable',
    load: () => import('@fontsource-variable/rubik/index.css'),
  },
  {
    id: 'tajawal',
    name: 'Tajawal',
    latin: 'Tajawal',
    arabic: 'Tajawal',
    // Tajawal ships no 600 — the browser resolves it up to 700.
    load: () => Promise.all([
      import('@fontsource/tajawal/400.css'),
      import('@fontsource/tajawal/500.css'),
      import('@fontsource/tajawal/700.css'),
    ]),
  },
  {
    id: 'almarai',
    name: 'Almarai',
    latin: 'Almarai',
    arabic: 'Almarai',
    // Almarai ships 300/400/700/800 only.
    load: () => Promise.all([
      import('@fontsource/almarai/400.css'),
      import('@fontsource/almarai/700.css'),
    ]),
  },
  {
    id: 'readex',
    name: 'Readex Pro',
    latin: 'Readex Pro Variable',
    arabic: 'Readex Pro Variable',
    load: () => import('@fontsource-variable/readex-pro/index.css'),
  },
  {
    id: 'vazirmatn',
    name: 'Vazirmatn',
    latin: 'Vazirmatn Variable',
    arabic: 'Vazirmatn Variable',
    load: () => import('@fontsource-variable/vazirmatn/index.css'),
  },
  {
    // Kufi is a display face for Arabic; Latin stays on Geist so numbers and
    // codes keep their shape.
    id: 'kufi',
    name: 'Noto Kufi',
    latin: 'Geist Sans',
    arabic: 'Noto Kufi Arabic Variable',
    load: () => import('@fontsource-variable/noto-kufi-arabic/index.css'),
  },
  { id: 'system', name: 'System', labelKey: 'font.system', latin: 'system-ui', arabic: 'system-ui' },
];

export const DEFAULT_FONT = 'geist';
const FONT_KEY = 'erp-font';

const GENERIC = 'ui-sans-serif, system-ui, sans-serif';

/** Quote a family name only when it needs it — `system-ui` is a keyword. */
function q(family: string): string {
  return /\s/.test(family) ? `"${family}"` : family;
}

function stack(first: string, second: string): string {
  const head = first === second ? q(first) : `${q(first)}, ${q(second)}`;
  return `${head}, ${GENERIC}`;
}

/**
 * Kurdish needs its own Arabic-script face, whatever theme is picked.
 *
 * Cairo — the Arabic half of the DEFAULT theme, and of three others — has no
 * ڕ ڵ ۆ ێ. Those four letters are ordinary Kurdish, in words as common as
 * "کوردی" and "ڕێکخستن", and without them the browser falls back per glyph to
 * whatever the system offers: a Kurdish sentence rendered in two typefaces, or
 * tofu. Vazirmatn covers them, so Kurdish gets Vazirmatn and Arabic keeps the
 * theme's own face — the same call the sales terminal and the manager desktop
 * already made (see their fontStackFor).
 *
 * This deliberately overrides the operator's font choice for Kurdish text only.
 * Latin runs, --font-mono, and every other language are untouched.
 */
const KURDISH_LANGS = new Set(['ckb', 'kmr']);
const KURDISH_FACE = 'Vazirmatn Variable';
const KURDISH_FONT_ID = 'vazirmatn';

export function isKurdish(lang: string | null | undefined): boolean {
  return KURDISH_LANGS.has((lang ?? '').split('-')[0] ?? '');
}

/**
 * The two stacks a theme resolves to. Each script's family leads and the other
 * one follows BEFORE the generics: system-ui covers both scripts, so leaving it
 * to lead the fallback would win the per-glyph match and quietly undo the pick
 * for mixed Arabic/Latin text (same reasoning as storeTheme.fontVar).
 */
export function fontStacks(f: FontTheme, lang?: string): { sans: string; arabic: string } {
  // No lang (the picker's own previews) keeps the theme's pairing as authored.
  const arabic = isKurdish(lang) ? KURDISH_FACE : f.arabic;
  return { sans: stack(f.latin, arabic), arabic: stack(arabic, f.latin) };
}

export function findFont(id: string): FontTheme {
  return FONTS.find((f) => f.id === id) ?? FONTS[0]!;
}

/**
 * Override --font-sans / --font-sans-arabic on :root for the whole app.
 *
 * `lang` is passed explicitly by the languageChanged subscriber, because at that
 * moment `<html lang>` may not have been updated yet — useDirection sets it from
 * its own listener and the two orders are not guaranteed.
 */
export function applyFont(id: string, lang?: string): void {
  if (typeof document === 'undefined') return;
  const lng = lang ?? document.documentElement.lang;
  // Fetched here rather than by the caller: every path that applies a font for a
  // Kurdish UI needs the face present, and one of them is app boot.
  if (isKurdish(lng)) ensureFontLoaded(KURDISH_FONT_ID);
  const { sans, arabic } = fontStacks(findFont(id), lng);
  document.documentElement.style.setProperty('--font-sans', sans);
  document.documentElement.style.setProperty('--font-sans-arabic', arabic);
}

const requested = new Set<string>();

/** Fetch a theme's font files once. Safe to call on hover, boot, or select. */
export function ensureFontLoaded(id: string): void {
  if (requested.has(id)) return;
  requested.add(id);
  const f = FONTS.find((x) => x.id === id);
  if (!f?.load) return;
  void f.load().catch(() => requested.delete(id));
}

export function loadFontTheme(): string {
  try {
    const v = window.localStorage.getItem(FONT_KEY);
    if (v && FONTS.some((f) => f.id === v)) return v;
  } catch {
    /* ignore */
  }
  return DEFAULT_FONT;
}

export function saveFontTheme(id: string): void {
  try {
    window.localStorage.setItem(FONT_KEY, id);
  } catch {
    /* ignore */
  }
}
